<template>
  <div class="garden-pot" :class="`garden-pot--${band}`">
    <div class="garden-pot__plant" style="height: 120px">
      <!-- Mastery: full bloom + butterfly + sparkles -->
      <template v-if="band === 'mastery'">
        <svg class="plant-body" width="80" height="120" viewBox="0 0 80 120">
          <path d="M20,95 L25,115 L55,115 L60,95 Z" class="garden-pot__clay" />
          <rect x="17" y="90" width="46" height="8" rx="2" class="garden-pot__rim" />
          <ellipse cx="40" cy="90" rx="22" ry="3" class="garden-pot__soil garden-pot__soil--green" />
          <rect x="38" y="25" width="4" height="65" rx="2" class="garden-pot__stem" />
          <ellipse cx="28" cy="55" rx="12" ry="5" class="garden-pot__leaf" transform="rotate(-30 28 55)" />
          <ellipse cx="52" cy="50" rx="12" ry="5" class="garden-pot__leaf" transform="rotate(25 52 50)" />
          <ellipse cx="26" cy="40" rx="10" ry="4" class="garden-pot__leaf--light" transform="rotate(-20 26 40)" />
          <ellipse cx="54" cy="65" rx="10" ry="4" class="garden-pot__leaf--light" transform="rotate(20 54 65)" />
          <circle cx="40" cy="22" r="5" fill="#a855f7" />
          <circle cx="33" cy="18" r="5" fill="#c084fc" />
          <circle cx="47" cy="18" r="5" fill="#c084fc" />
          <circle cx="33" cy="26" r="5" fill="#c084fc" />
          <circle cx="47" cy="26" r="5" fill="#c084fc" />
          <circle cx="40" cy="22" r="3" fill="#fbbf24" />
          <circle cx="18" cy="15" r="2" fill="#fbbf24" class="sparkle-anim" />
          <circle cx="62" cy="10" r="1.5" fill="#fbbf24" class="sparkle-anim-delay" />
          <circle cx="55" cy="30" r="1.5" fill="#fbbf24" class="sparkle-anim-delay2" />
        </svg>
        <svg class="butterfly-anim" width="24" height="20" viewBox="0 0 24 20">
          <ellipse cx="8" cy="8" rx="6" ry="7" fill="#c084fc" opacity="0.8" />
          <ellipse cx="16" cy="8" rx="6" ry="7" fill="#a78bfa" opacity="0.8" />
          <ellipse cx="8" cy="14" rx="4" ry="5" fill="#d8b4fe" opacity="0.7" />
          <ellipse cx="16" cy="14" rx="4" ry="5" fill="#c4b5fd" opacity="0.7" />
          <rect x="11.5" y="4" width="1" height="14" rx="0.5" fill="#581c87" />
          <circle cx="12" cy="4" r="1" fill="#581c87" />
        </svg>
      </template>

      <!-- Confident: open flower -->
      <template v-else-if="band === 'confident'">
        <svg class="plant-body" width="80" height="120" viewBox="0 0 80 120">
          <path d="M20,95 L25,115 L55,115 L60,95 Z" class="garden-pot__clay" />
          <rect x="17" y="90" width="46" height="8" rx="2" class="garden-pot__rim" />
          <ellipse cx="40" cy="90" rx="22" ry="3" class="garden-pot__soil garden-pot__soil--green" />
          <rect x="38" y="30" width="4" height="60" rx="2" class="garden-pot__stem" />
          <ellipse cx="28" cy="58" rx="12" ry="5" class="garden-pot__leaf" transform="rotate(-30 28 58)" />
          <ellipse cx="52" cy="52" rx="12" ry="5" class="garden-pot__leaf" transform="rotate(25 52 52)" />
          <ellipse cx="30" cy="44" rx="10" ry="4" class="garden-pot__leaf--light" transform="rotate(-15 30 44)" />
          <circle cx="40" cy="27" r="4.5" fill="#f97316" />
          <circle cx="34" cy="24" r="4" fill="#fb923c" />
          <circle cx="46" cy="24" r="4" fill="#fb923c" />
          <circle cx="34" cy="31" r="4" fill="#fb923c" />
          <circle cx="46" cy="31" r="4" fill="#fb923c" />
          <circle cx="40" cy="27" r="2.5" fill="#fbbf24" />
        </svg>
      </template>

      <!-- Secure: flower bud -->
      <template v-else-if="band === 'secure'">
        <svg class="plant-body" width="80" height="120" viewBox="0 0 80 120">
          <path d="M20,95 L25,115 L55,115 L60,95 Z" class="garden-pot__clay" />
          <rect x="17" y="90" width="46" height="8" rx="2" class="garden-pot__rim" />
          <ellipse cx="40" cy="90" rx="22" ry="3" class="garden-pot__soil garden-pot__soil--green" />
          <rect x="38" y="42" width="4" height="48" rx="2" class="garden-pot__stem" />
          <ellipse cx="30" cy="62" rx="10" ry="4.5" class="garden-pot__leaf" transform="rotate(-25 30 62)" />
          <ellipse cx="50" cy="56" rx="10" ry="4.5" class="garden-pot__leaf" transform="rotate(25 50 56)" />
          <ellipse cx="40" cy="40" rx="5" ry="7" fill="#facc15" />
          <ellipse cx="37" cy="42" rx="3" ry="6" fill="#65a30d" transform="rotate(10 37 42)" />
          <ellipse cx="43" cy="42" rx="3" ry="6" fill="#65a30d" transform="rotate(-10 43 42)" />
        </svg>
      </template>

      <!-- Developing: sprout -->
      <template v-else-if="band === 'developing'">
        <svg class="plant-body" width="80" height="120" viewBox="0 0 80 120">
          <path d="M20,95 L25,115 L55,115 L60,95 Z" class="garden-pot__clay" />
          <rect x="17" y="90" width="46" height="8" rx="2" class="garden-pot__rim" />
          <ellipse cx="40" cy="90" rx="22" ry="3" class="garden-pot__soil garden-pot__soil--brown" />
          <rect x="39" y="68" width="3" height="22" rx="1.5" fill="#22c55e" />
          <ellipse cx="33" cy="72" rx="7" ry="3.5" fill="#4ade80" transform="rotate(-30 33 72)" />
          <ellipse cx="47" cy="70" rx="7" ry="3.5" fill="#4ade80" transform="rotate(30 47 70)" />
        </svg>
      </template>

      <!-- Beginner: seed -->
      <template v-else>
        <svg class="plant-body" width="80" height="120" viewBox="0 0 80 120">
          <path d="M20,95 L25,115 L55,115 L60,95 Z" class="garden-pot__clay" />
          <rect x="17" y="90" width="46" height="8" rx="2" class="garden-pot__rim" />
          <ellipse cx="40" cy="90" rx="22" ry="3" class="garden-pot__soil garden-pot__soil--brown" opacity="0.5" />
          <ellipse cx="40" cy="86" rx="4" ry="3" fill="#9ca3af" />
        </svg>
      </template>
    </div>
    <p class="garden-pot__name">{{ topicName }}</p>
    <span class="garden-pot__badge" :class="`garden-pot__badge--${band}`">{{ bandLabel }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  band: { type: String, default: 'beginner' },
  topicName: { type: String, required: true }
});

const bandLabels = {
  beginner: 'Beginner',
  developing: 'Developing',
  secure: 'Secure',
  confident: 'Confident',
  mastery: 'Mastery'
};

const bandLabel = computed(() => bandLabels[props.band] || 'Beginner');
</script>

<style scoped>
.garden-pot {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.garden-pot:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.garden-pot:hover .plant-body {
  animation: sway 1.2s ease-in-out infinite;
  transform-origin: bottom center;
}

.garden-pot__plant {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

.garden-pot__clay { fill: #a16207; stroke: #854d0e; stroke-width: 1; }
.garden-pot__rim { fill: #ca8a04; stroke: #a16207; stroke-width: 1; }
.garden-pot__stem { fill: #16a34a; }
.garden-pot__leaf { fill: #22c55e; }
.garden-pot__leaf--light { fill: #4ade80; }
.garden-pot__soil--green { fill: #65a30d; opacity: 0.3; }
.garden-pot__soil--brown { fill: #8B6914; opacity: 0.3; }

.garden-pot__name {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--mt-text-primary, #1f2937);
  margin-top: 0.25rem;
  text-align: center;
  line-height: 1.2;
}
.garden-pot__badge {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
}
.garden-pot__badge--beginner { background: #dbeafe; color: #1d4ed8; }
.garden-pot__badge--developing { background: #dcfce7; color: #15803d; }
.garden-pot__badge--secure { background: #fef9c3; color: #a16207; }
.garden-pot__badge--confident { background: #ffedd5; color: #c2410c; }
.garden-pot__badge--mastery { background: #f3e8ff; color: #7e22ce; }

.butterfly-anim {
  position: absolute;
  top: 0;
  right: 0.25rem;
  animation: butterfly-float 3s ease-in-out infinite;
}

.sparkle-anim { animation: sparkle 1.5s ease-in-out infinite; }
.sparkle-anim-delay { animation: sparkle 1.5s ease-in-out infinite 0.5s; }
.sparkle-anim-delay2 { animation: sparkle 1.5s ease-in-out infinite 1s; }

@keyframes sway {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}
@keyframes butterfly-float {
  0%, 100% { transform: translate(0, 0) rotate(-5deg); }
  25% { transform: translate(4px, -6px) rotate(5deg); }
  50% { transform: translate(8px, -2px) rotate(-3deg); }
  75% { transform: translate(2px, -8px) rotate(4deg); }
}
@keyframes sparkle {
  0%, 100% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 1; transform: scale(1); }
}
</style>

<!-- Theme overrides (non-scoped) -->
<style>
/* Space theme: neon glow plants */
.theme-space .garden-pot {
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(139, 92, 246, 0.2);
}
.theme-space .garden-pot:hover {
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.2);
}
.theme-space .garden-pot__name { color: #e2e8f0; }
.theme-space .garden-pot__clay { fill: #44403c; stroke: #57534e; }
.theme-space .garden-pot__rim { fill: #57534e; stroke: #78716c; }
.theme-space .garden-pot__stem { fill: #059669; filter: drop-shadow(0 0 3px #34d399); }
.theme-space .garden-pot__leaf { fill: #10b981; filter: drop-shadow(0 0 3px #6ee7b7); }
.theme-space .garden-pot__leaf--light { fill: #34d399; opacity: 0.8; }
.theme-space .garden-pot__soil--green { fill: #22c55e; opacity: 0.15; }
.theme-space .garden-pot__badge--beginner { background: rgba(59, 130, 246, 0.2); color: #93c5fd; border: 1px solid rgba(59, 130, 246, 0.3); }
.theme-space .garden-pot__badge--developing { background: rgba(34, 197, 94, 0.2); color: #86efac; border: 1px solid rgba(34, 197, 94, 0.3); }
.theme-space .garden-pot__badge--secure { background: rgba(250, 204, 21, 0.2); color: #fde047; border: 1px solid rgba(250, 204, 21, 0.3); }
.theme-space .garden-pot__badge--confident { background: rgba(249, 115, 22, 0.2); color: #fdba74; border: 1px solid rgba(249, 115, 22, 0.3); }
.theme-space .garden-pot__badge--mastery { background: rgba(168, 85, 247, 0.2); color: #d8b4fe; border: 1px solid rgba(168, 85, 247, 0.3); }

/* Trail theme: earthy tones */
.theme-trail .garden-pot {
  background: #fefce8;
  border: 1px solid #d6c89e;
}
.theme-trail .garden-pot__clay { fill: #92400e; stroke: #78350f; }
.theme-trail .garden-pot__rim { fill: #b45309; stroke: #92400e; }

/* Road theme: teal accents */
.theme-road .garden-pot {
  background: #f0fdfa;
  border: 1px solid #99f6e4;
}
.theme-road .garden-pot__badge--secure { background: #ccfbf1; color: #0f766e; }
.theme-road .garden-pot__badge--confident { background: #fed7aa; color: #9a3412; }
</style>
